/**
 * Discrete grid-cell "heatmap" layer, fed by the MarkLogic box-grid
 * aggregation (see `utils/heatmapUtil.js` and sensingcluesr::get_heatmap
 * v1.1.0). Each backend box becomes a rectangular fill cell whose colour is
 * driven by the cell's normalised `intensity` (0..1), giving a faithful
 * raster-style heatmap rather than a smooth Mapbox density blob.
 *
 * Generic over `layerName` so the exact same functions serve every layer that
 * adopts this mechanism (Observations and Tracks) - keep it maintained in this
 * one file rather than duplicating per layer.
 *
 * These functions are mixed into `MapComponent` and rely on `this.map`
 * (the Mapbox GL instance), matching the pattern used by the other
 * `mapComponent/*` handler modules.
 */

// Graduated fill colour ramp on the per-cell normalised intensity (0..1),
// mirroring the classic blue->red heat palette.
// Builds a Mapbox `interpolate` fill-colour expression on the per-cell
// normalised intensity (0..1) from a list of [stop, colour] pairs.
const buildColorRamp = stops => {
  const expr = ['interpolate', ['linear'], ['get', 'intensity']];
  stops.forEach(([stop, colour]) => {
    expr.push(stop, colour);
  });
  return expr;
};

// Sequential colour scales (ColorBrewer anchor samples), light = few,
// dark/warm = many. Warm yellow-red for observations vs cool light->dark
// blue for tracks gives a clear cool<->warm contrast; both are sequential
// so they convey amount, not a good/bad judgement.
const OBSERVATION_SCALE = buildColorRamp([
  // YlOrRd
  [0, '#ffffb2'],
  [0.2, '#fed976'],
  [0.4, '#feb24c'],
  [0.6, '#fd8d3c'],
  [0.8, '#f03b20'],
  [1, '#bd0026']
]);
const TRACK_SCALE = buildColorRamp([
  // Blues only, low density = light blue (e.g. a single pass), high
  // density = dark blue (many passes) - shifted a few stops darker than
  // the standard ColorBrewer Blues 6-class ramp so the top end reads as a
  // genuinely dark navy blue rather than a mid-tone blue. No warm/yellow
  // tones.
  [0, '#c6dbef'],
  [0.2, '#9ecae1'],
  [0.4, '#6baed6'],
  [0.6, '#4292c6'],
  [0.8, '#2171b5'],
  [1, '#08306b']
]);

// Per-layer colour scale: Observations -> YlOrRd, Tracks -> Blues.
const HEATMAP_FILL_COLORS = {
  Observations: OBSERVATION_SCALE,
  Tracks: TRACK_SCALE
};
const DEFAULT_FILL_COLOR = OBSERVATION_SCALE;

export function getHeatmapSourceId(layerName) {
  return `${layerName}_heatmap`;
}

export function getHeatmapLayerId(layerName) {
  return `${layerName}_heatmap_layer`;
}

export function getHeatmapOutlineLayerId(layerName) {
  return `${layerName}_heatmap_outline`;
}

/**
 * Adds the (initially empty) heatmap grid source + fill/outline layers for a
 * given map layer name, if they don't already exist.
 * @param {string} layerName - e.g. 'Observations' or 'Tracks'.
 * @param {string} visibility - 'visible' | 'none'.
 * @param {Array} [fillColor] - optional Mapbox expression overriding the default colour ramp.
 * @param {number} [opacity] - initial fill-opacity (0.1-0.9, see the transparency
 *   slider in the layer options menu). Only applied when the layer is first
 *   created - use setHeatmapOpacity() to update it live afterwards.
 */
export function addHeatmapLayer(
  layerName,
  visibility = 'none',
  fillColor,
  opacity = 0.3
) {
  if (!this.map) return;
  const ramp =
    fillColor || HEATMAP_FILL_COLORS[layerName] || DEFAULT_FILL_COLOR;
  const initialOpacity = typeof opacity === 'number' ? opacity : 0.3;
  const sourceId = getHeatmapSourceId(layerName);
  const layerId = getHeatmapLayerId(layerName);
  const outlineId = getHeatmapOutlineLayerId(layerName);

  if (!this.map.getSource(sourceId)) {
    this.map.addSource(sourceId, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });
  }

  if (!this.map.getLayer(layerId)) {
    this.map.addLayer({
      id: layerId,
      type: 'fill',
      source: sourceId,
      layout: { visibility },
      paint: {
        'fill-color': ramp,
        'fill-opacity': initialOpacity
      }
    });
  } else {
    this.map.setLayoutProperty(layerId, 'visibility', visibility);
  }

  if (!this.map.getLayer(outlineId)) {
    this.map.addLayer({
      id: outlineId,
      type: 'line',
      source: sourceId,
      layout: { visibility },
      paint: {
        'line-color': 'rgba(80,80,80,0.35)',
        'line-width': 0.5
      }
    });
  } else {
    this.map.setLayoutProperty(outlineId, 'visibility', visibility);
  }
}

/**
 * Pushes a freshly parsed grid GeoJSON FeatureCollection into the map source.
 * @param {string} layerName
 * @param {{type: string, features: Array}} geojson
 */
export function updateHeatmapSource(layerName, geojson) {
  if (!this.map) return;
  const source = this.map.getSource(getHeatmapSourceId(layerName));
  if (source && geojson) {
    source.setData(geojson);
  }
}

/**
 * Shows/hides the heatmap grid (fill + outline) for a given map layer name.
 * @param {string} layerName
 * @param {string} visibility - 'visible' | 'none'.
 */
export function setHeatmapVisibility(layerName, visibility) {
  if (!this.map) return;
  [getHeatmapLayerId(layerName), getHeatmapOutlineLayerId(layerName)].forEach(
    id => {
      if (this.map.getLayer(id)) {
        this.map.setLayoutProperty(id, 'visibility', visibility);
      }
    }
  );
}

/**
 * Live-updates the fill transparency of an already-created heatmap grid (the
 * "transparency" slider in the layer options menu) - a pure paint-property
 * change, no refetch needed. No-op if the layer hasn't been created yet;
 * addHeatmapLayer()'s own `opacity` param covers that first-paint case.
 * @param {string} layerName
 * @param {number} opacity - 0.1-0.9.
 */
export function setHeatmapOpacity(layerName, opacity) {
  if (!this.map) return;
  const layerId = getHeatmapLayerId(layerName);
  if (this.map.getLayer(layerId)) {
    this.map.setPaintProperty(layerId, 'fill-opacity', opacity);
  }
}
